/**
 * StandaloneNetWorld - no-op NetWorld for single-player/offline mode.
 * No replication, rejects multiplayer API calls. Used when no server URL configured.
 */



import { NetWorld } from './NetWorld.js';

import type { PlayerController } from '../actors/PlayerController.js';


/**
 * No-op `NetWorld` for standalone (offline / single-player) mode.
 */
export class StandaloneNetWorld extends NetWorld {
  private localPlayerControllerPromise: Promise<PlayerController | null> = Promise.resolve(null);

  constructor(gameId: string) {
    super(gameId);
    console.log('[StandaloneNetWorld] Using standalone (offline) mode');
  }

  protected override doEndPlay(): void {
    super.doEndPlay();
    this.localPlayerControllerPromise = Promise.resolve(null);
  }

  /**
   * Spawns the local player via `GameMode.onPlayerJoined()` once the world is ready.
   * This replaces the old polling-based `spawnLocalPlayerControllerIfNeeded` pattern.
   */
  protected override doBeginPlay(): void {
    super.doBeginPlay();
    const gameMode = this.world?.gameMode;
    if (gameMode) {
      this.localPlayerControllerPromise =  gameMode.onPlayerJoined(this.clientId);
    }
  }

  public override async waitForLocalPlayerController(): Promise<PlayerController | null> {
    return this.localPlayerControllerPromise;
  }

  /**
   * No-op tick — there is nothing to replicate in standalone mode.
   * @param _deltaTime - Unused.
   */
  public override async tick(_deltaTime: number): Promise<void> {
    // No replication in standalone mode.
  }

  /**
   * @throws Always. Standalone mode cannot host a server.
   *   Use `NetRuntime.isDedicatedServer()` to guard server-only code.
   */
  public override async startServer(_portRange: [number, number]): Promise<number> {
    throw new Error(
      '[StandaloneNetWorld] startServer() is not available in standalone mode. ' +
      'Only dedicated-server instances may host a WebSocket server.',
    );
  }
}

